import Vue from 'vue'
import App from './App.vue'
import store from './store'
import VueRouter from "vue-router";
import IaaiService from "@/IaaiService";
import * as Sentry from "@sentry/vue";

const jquery = require('../node_modules/jquery/dist/jquery.min')
window.$ = window.jQuery = jquery

const signalr = require('../node_modules/signalr/jquery.signalR.min');
window.signalr = signalr

Vue.config.productionTip = false;

const routes = [
    {path: '/', component: App}
]

const router = new VueRouter({
    mode: 'history',
    routes
})

Vue.use(VueRouter)

// Router error handling
router.onError((error) => {
    console.error('Router Error:', error);

    Sentry.captureException(error, {
        level: 'error',
        tags: {
            errorType: 'router_error',
        },
        contexts: {
            router: {
                currentRoute: router.currentRoute?.path || 'unknown',
                currentRouteName: router.currentRoute?.name || 'unknown',
            }
        },
        extra: {
            errorMessage: error.message,
        }
    });
});

Vue.prototype.$iaaiService = new IaaiService()

function getCookie(name) {
    let matches = document.cookie.match(new RegExp(
        "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + "=([^;]*)"
    ));
    return matches ? decodeURIComponent(matches[1]) : undefined;
}

// Initialize Sentry with enhanced error tracking
Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV || 'production',

    // Filter out errors from AuctionNow.js (SignalR library) by URL
    denyUrls: [
        /AuctionNow\.js/i,
        // Browser extensions (obfuscated code)
        /webkit-masked-url/i,
    ],

    // Filter out known error messages
    ignoreErrors: [
        // SignalR connection errors
        'Server timeout elapsed without receiving a message',
        'Cannot send data if the connection is not in the',
        /Failed to complete negotiation with the server/,
        'Server returned an error on close',
        'Client hasn\'t sent a message/ping within the configured ClientTimeoutInterval',
        'Invocation canceled due to the underlying connection being closed',
        /Unable to connect to the server with any of the available transports/,
        /WebSocket failed to connect/,
        /The connection could not be found on the server/,
        // WebSocket errors
        /WebSocket closed with status code: 1006/,
        /WebSocket closed with status code: 1000/,
        /WebSocket is not in the OPEN state/,
        // XMLHttpRequest errors (malformed error objects)
        /\[object XMLHttpRequest\]/,
        // AuctionNow business logic
        /Already joined auction, leave it first/,
        /Farm not active for auction/,
        /FarmNotActiveForAuctionException/,
        // PingAuctionLevel errors (various formats)
        /PingAuctionLevel.*Farm not active/,
        /PingAuctionLevel.*FarmNotActiveForAuctionException/,
        /An unexpected error occurred invoking 'PingAuctionLevel'.*Farm not active/,
        /An unexpected error occurred invoking 'PingAuctionLevel'.*FarmNotActiveForAuctionException/,
        /ReplayFrom.*Farm not active/,
        /ReplayFrom.*FarmNotActiveForAuctionException/,
        // PlaceBid validation errors
        /An unexpected error occurred invoking 'PlaceBid'.*CommandErrorException.*BidAmount/,
        /An unexpected error occurred invoking 'PlaceBid'.*BidAmount/,
        /PlaceBid.*CommandErrorException.*BidAmount/,
        /CommandErrorException.*BidAmount/,
        /CommandErrorException.*CurrentHighBidder/,
        /CommandErrorException.*AccessDeniedToBidderId/,
        // AuctionNow connection errors
        /No Connection with that ID/,
        /Status code '404'/,
        // AuctionNow message parsing errors
        /Message is incomplete/,
        /JSON Parse error.*Unterminated string/,
        // AuctionNow SSE transport errors
        /TypeError: Load failed/,
        /Load failed/,
        // Browser errors
        /AbortError.*operation was aborted/,
        // Browser extensions errors (obfuscated variables)
        /Can't find variable: _0x/,
        /Cannot read property '_0x/,
        // Browser extensions runtime errors
        /Invalid call to runtime\.sendMessage/,
        /Tab not found/,
        // Stack overflow errors (often from browser extensions or minified code)
        /Maximum call stack size exceeded/,
    ],

    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
        }),
    ],

    // Performance monitoring
    tracesSampleRate: 0.1,

    // Session replay settings
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event) {
        // Don't send events from localhost:8000
        if (window.location.origin === 'http://localhost:8000') {
            return null;
        }

        // Enrich with user context from store
        try {
            const userId = store.getters['userId'];
            if (userId) {
                event.user = {
                    id: userId,
                };
            }

            // Add current route information
            if (router.currentRoute) {
                event.tags = event.tags || {};
                event.tags.route = router.currentRoute.path;
            }
        } catch (e) {
            console.error('Error enriching Sentry event:', e);
        }

        return event;
    }
});

// Global Vue error handler
Vue.config.errorHandler = (err, vm, info) => {
    console.error('Vue Error:', err, info);

    Sentry.captureException(err, {
        level: 'error',
        tags: {
            errorType: 'vue_error',
            lifecycleHook: info || 'unknown',
        },
        contexts: {
            vue: {
                componentName: vm?.$options?.name || 'Unknown Component',
                propsData: vm?.$options?.propsData,
                lifecycleHook: info,
            }
        }
    });
};

// Global unhandled promise rejection handler
window.addEventListener('unhandledrejection', event => {
    console.error('Unhandled Promise Rejection:', event.reason);

    // Send to Sentry, beforeSend hook will filter AuctionNow.js errors
    Sentry.captureException(event.reason, {
        level: 'error',
        tags: {
            errorType: 'unhandled_promise_rejection',
        },
        contexts: {
            promise: {
                type: 'unhandled_rejection',
                reason: String(event.reason),
            }
        }
    });
});

new Vue({
    store,
    router,
    render: h => h(App),
    beforeCreate() {
        const bearerToken = this.$route.query.token || getCookie('token');
        this.$iaaiService.init(bearerToken);
        document.cookie = `token=${bearerToken}`;
    }
}).$mount('#app')
